import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Loader2, Check, X, AlertCircle, ChevronRight, User, ChevronsUpDown } from 'lucide-react';
import { useTenancy } from '@/contexts/TenancyContext';
import type { Tenancy } from '@/contexts/TenancyContext';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { TenancyQuickSwitcher } from '@/components/TenancyQuickSwitcher';
import { useTheme } from '@/contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faTicket, faEnvelope, faBuilding, faUserGear, faExchangeAlt, faTrash, faExclamationTriangle } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { BeautifulTabsSelector } from '@/components/ui/BeautifulTabsSelector';
import { db } from '@/lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface MSPCustomer {
  customerID: string;
  customerFriendlyName: string;
  customerOwner: string;
  type: string;
  mspDetails: {
    techPartner?: {
      impersonationAllowed: boolean;
    };
    businessPartner?: {
      impersonationAllowed: boolean;
    };
    impersonationAllowed?: boolean;
  };
  logoUrl?: string;
}

export const MSPSettingsSection = () => {
  const { activeTenancy, setActiveTenancy, tenancies, loading } = useTenancy();
  const { mode } = useTheme();

  const [customers, setCustomers] = useState<MSPCustomer[]>([]);
  const [ticketingEmail, setTicketingEmail] = useState('');
  const [ticketingEmailEnabled, setTicketingEmailEnabled] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [accessingTenancy, setAccessingTenancy] = useState<string | null>(null);
  const [customerToRemove, setCustomerToRemove] = useState<MSPCustomer | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [isQuickSwitcherOpen, setIsQuickSwitcherOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'customers' | 'ticketing'>('customers');

  // Map real tenancies from context to MSPCustomer model
  useEffect(() => {
    if (!tenancies) return;
    const mapped: MSPCustomer[] = tenancies.map((t) => {
      // derive type from impersonation flags; prefer tech, then business, else general
      const isTech = !!t.impersonationAllowed;
      const isBusiness = !!t.impersonationAllowedBusiness;
      const type: 'tech' | 'business' | 'general' = isTech ? 'tech' : isBusiness ? 'business' : 'general';

      return {
        customerID: t.id,
        customerFriendlyName: t.name,
        customerOwner: t.tenancyOwner || '',
        type,
        mspDetails: {
          techPartner: { impersonationAllowed: !!t.impersonationAllowed },
          businessPartner: { impersonationAllowed: !!t.impersonationAllowedBusiness },
          impersonationAllowed: !!t.impersonationAllowed || !!t.impersonationAllowedBusiness,
        },
        logoUrl: t.logo || '',
      };
    });
    setCustomers(mapped);
  }, [tenancies]);

  useEffect(() => {
    if (!activeTenancy) return;
    // Load ticketing email settings
    const ticketingEmailDoc = getDoc(doc(db, `customers/${activeTenancy.id}/companySettings/integrations`));
    ticketingEmailDoc.then((doc) => {
      const ticketingEmailData = doc.data();
      if (ticketingEmailData && ticketingEmailData.ticketingEmail) {
        setTicketingEmailEnabled(ticketingEmailData.ticketingEmail.Enabled.value);
        setTicketingEmail(ticketingEmailData.ticketingEmail.ticketingEmail);
      }
    });
    
    // setIsLoading(false);
  }, []);

  const handleSaveTicketingEmail = async () => {
    setSaveStatus("saving");

    try {
      // Simulate API call to save ticketing email settings
      // In a real implementation, this would call the actual API
      // await new Promise(resolve => setTimeout(resolve, 1000));
      const data = {
        ticketingEmail: {
          Enabled: {
            details: null,
            settingName: "Enabled",
            value: ticketingEmailEnabled
          },
          ticketingEmail: ticketingEmail
        }
      };
    
      await 
        setDoc(doc(db, `customers/${activeTenancy.id}/companySettings/integrations`), data, { merge: true });

      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 2000);
    } catch (error) {
      console.error("Error saving ticketing email:", error);
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 2000);
    }
  };

  const getImpersonationStatus = (customer: MSPCustomer): boolean => {
    if (customer.type === 'tech' && customer.mspDetails.techPartner) {
      return customer.mspDetails.techPartner.impersonationAllowed;
    } else if (customer.type === 'business' && customer.mspDetails.businessPartner) {
      return customer.mspDetails.businessPartner.impersonationAllowed;
    } else if (customer.type === 'general' && customer.mspDetails.impersonationAllowed !== undefined) {
      return customer.mspDetails.impersonationAllowed;
    }
    return false;
  };

  const handleAccessTenancy = async (customer: MSPCustomer) => {
    setAccessingTenancy(customer.customerID);

    try {
      // Find the matching tenancy from the context
      const targetTenancy = tenancies.find(t => t.id === customer.customerID);

      if (targetTenancy) {
        // Use the TenancyContext to switch tenancies
        setActiveTenancy(targetTenancy);

        // Show success message or update UI as needed
        console.log(`Switched to ${targetTenancy.name}`);
      } else {
        console.error('Tenancy not found:', customer.customerID);
      }
    } catch (error) {
      console.error("Error accessing tenancy:", error);
    } finally {
      setAccessingTenancy(null);
    }
  };

  // Handle tenancy switch from quick switcher
  const handleQuickSwitch = (tenancy: Tenancy) => {
    setActiveTenancy(tenancy);
    setIsQuickSwitcherOpen(false);
  };

  const handleRemoveCustomer = async () => {
    if (!customerToRemove || confirmText !== customerToRemove.customerFriendlyName) {
      return;
    }

    setIsRemoving(true);

    try {
      // In a real implementation, this would call the actual API to remove the customer from MSP management
      // and switch them to customer-owned billing
      await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate API call

      // Update the local state to remove the customer from the list
      setCustomers(prev => prev.filter(c => c.customerID !== customerToRemove.customerID));

      // Reset state
      setCustomerToRemove(null);
      setConfirmText("");
    } catch (error) {
      console.error("Error removing customer from MSP:", error);
    } finally {
      setIsRemoving(false);
    }
  };

  // Tenancies for the quick switcher come directly from context

  const MSP_TABS = [
    {
      group: "MSP Settings",
      options: [
        { value: "customers", label: "Customers" },
        { value: "ticketing", label: "Ticketing" },
      ],
    }
  ];

  return (
    <div className="w-full">
      <div className="bg-card rounded-xl border border-border shadow-sm p-6 w-full">
        <div className="flex justify-between items-center align-center mb-6">
          <div className="flex flex-col">
            <BeautifulTabsSelector
              options={MSP_TABS}
              value={selectedTab}
              onChange={(v) => setSelectedTab(v as 'customers' | 'ticketing')}
              selectorLabel="MSP Settings"
            />
            <p className="text-sm text-muted-foreground">Set up and customize your MSP settings & integrations.</p>
          </div>
          {/* Quick Switcher Button */}
          <Button
            variant="outline"
            size="sm"
            className="ml-auto mr-2"
            onClick={() => setIsQuickSwitcherOpen(true)}
          >
            <ChevronsUpDown className="h-4 w-4 mr-2" />
            Switch Customer
          </Button>

          {/* Tenancy Quick Switcher */}
          <TenancyQuickSwitcher
            tenancies={tenancies}
            onSwitch={handleQuickSwitch}
            open={isQuickSwitcherOpen}
            onOpenChange={setIsQuickSwitcherOpen}
          />
        </div>
        <Tabs value={selectedTab} onValueChange={(v) => setSelectedTab(v as 'customers' | 'ticketing')} className="w-full">

          <TabsContent value="customers">
            {loading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : customers.length === 0 ? (
              <div className="text-center py-12">
                <FontAwesomeIcon icon={faBuilding} className="text-muted-foreground text-4xl mb-4" />
                <p className="text-muted-foreground">No customers found</p>
              </div>
            ) : (
              <div className="rounded-lg overflow-x-auto">
                <table className="w-full text-sm min-w-[800px] table">
                  <thead className="bg-muted/50">
                    <tr className="h-10">
                      <th className="w-16 px-2 text-center font-medium text-muted-foreground">Logo</th>
                      <th className="min-w-[200px] px-3 text-left font-medium text-muted-foreground">Customer Name</th>
                      <th className="w-48 px-3 text-left font-medium text-muted-foreground">Impersonation</th>
                      <th className="min-w-[200px] px-3 text-left font-medium text-muted-foreground">Owner</th>
                      <th className="w-48 px-3 text-right font-medium text-muted-foreground whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {customers.map((customer) => (
                      <tr key={customer.customerID} className="bg-card hover:bg-muted/20 transition-colors">
                        <td className="px-2 py-2">
                          <div className="h-8 w-8 mx-auto rounded-full overflow-hidden bg-muted flex items-center justify-center">
                            {customer.logoUrl ? (
                              <img src={customer.logoUrl} alt="" className="h-full w-full object-cover" />
                            ) : (
                              <FontAwesomeIcon icon={faBuilding} className="text-muted-foreground text-sm" />
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 font-medium text-foreground truncate max-w-xs" title={customer.customerFriendlyName}>
                          {customer.customerFriendlyName}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium w-12 ${getImpersonationStatus(customer)
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                            {getImpersonationStatus(customer) ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-muted-foreground truncate max-w-xs" title={customer.customerOwner}>
                          {customer.customerOwner}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={() => handleAccessTenancy(customer)}
                              disabled={!getImpersonationStatus(customer) || accessingTenancy === customer.customerID}
                              className={`inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${getImpersonationStatus(customer)
                                ? 'bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-900/50'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800/50 dark:text-gray-500'
                                }`}
                            >
                              {accessingTenancy === customer.customerID ? (
                                <span className="flex items-center gap-1">
                                  <div className="animate-spin h-3 w-3 border-b-2 border-current rounded-full"></div>
                                  Accessing...
                                </span>
                              ) : (
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faExchangeAlt} className="text-xs" />
                                  Access
                                </span>
                              )}
                            </button>

                            <Dialog>
                              <DialogTrigger asChild>
                                <button
                                  onClick={() => setCustomerToRemove(customer)}
                                  className="inline-flex items-center rounded-md px-2.5 py-1.5 text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50 transition-colors"
                                >
                                  <FontAwesomeIcon icon={faTrash} className="text-xs mr-1" />
                                  Remove
                                </button>
                              </DialogTrigger>
                              <DialogContent className="sm:max-w-md bg-white dark:bg-gray-900 border-border">
                                <DialogHeader>
                                  <DialogTitle className="text-destructive flex items-center gap-2">
                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                    Danger: Remove Customer from MSP
                                  </DialogTitle>
                                  <DialogDescription>
                                    <p className="mt-2 mb-4">
                                      You are about to remove <strong>{customerToRemove?.customerFriendlyName}</strong> from your MSP management.
                                      This action will transfer billing responsibility directly to the customer and remove your MSP access privileges.
                                    </p>
                                    <div className="bg-destructive/10 border border-destructive/20 rounded-md p-3 mb-4">
                                      <p className="text-destructive text-sm font-medium">
                                        Warning: This will result in:
                                      </p>
                                      <ul className="list-disc list-inside text-sm mt-2 space-y-1 text-destructive/90">
                                        <li>Customer being switched to customer-owned billing</li>
                                        <li>Removal of all MSP management privileges</li>
                                        <li>Loss of access to customer's tenancy and data</li>
                                        <li>Customer being notified of the management change</li>
                                      </ul>
                                    </div>
                                  </DialogDescription>
                                </DialogHeader>

                                <div className="space-y-4 py-2">
                                  <div className="space-y-2">
                                    <label htmlFor="confirmText" className="text-sm font-medium">
                                      Type <strong>{customerToRemove?.customerFriendlyName}</strong> to confirm removal:
                                    </label>
                                    <Input
                                      id="confirmText"
                                      value={confirmText}
                                      onChange={(e) => setConfirmText(e.target.value)}
                                      className="border-destructive/50 focus-visible:ring-destructive"
                                      placeholder={customerToRemove?.customerFriendlyName}
                                    />
                                  </div>
                                </div>

                                <DialogFooter className="sm:justify-between">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => {
                                      setCustomerToRemove(null);
                                      setConfirmText("");
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    disabled={confirmText !== customerToRemove?.customerFriendlyName || isRemoving}
                                    onClick={handleRemoveCustomer}
                                  >
                                    {isRemoving ? (
                                      <span className="flex items-center gap-2">
                                        <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full"></div>
                                        Processing...
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-2">
                                        <FontAwesomeIcon icon={faTrash} />
                                        Remove from MSP
                                      </span>
                                    )}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ticketing" className="space-y-6">
            <div className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="ticketingEmailEnabledInput" className="block text-sm font-medium text-foreground">
                  Ticketing Email Enabled
                </label>
                <Select
                  value={ticketingEmailEnabled ? "true" : "false"}
                  onValueChange={(value) => setTicketingEmailEnabled(value === "true")}
                >
                  <SelectTrigger id="ticketingEmailEnabledInput" className="w-full">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">Disabled</SelectItem>
                    <SelectItem value="true">Enabled</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Indicates whether the MSP's ticketing email is being used for Assurance, Asset, or Risk notifications.
                  For example, if set to "Enabled," the Ticketing Email is active.
                </p>
              </div>

              <div className="space-y-2">
                <label htmlFor="ticketingEmailInput" className="block text-sm font-medium text-foreground">
                  Ticketing Email
                </label>
                <input
                  type="email"
                  id="ticketingEmailInput"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="Enter ticketing email"
                  value={ticketingEmail}
                  onChange={(e) => setTicketingEmail(e.target.value)}
                  disabled={!ticketingEmailEnabled}
                />
                <p className="text-xs text-muted-foreground">
                  If anyone in your organisation is assigned an Assurance Task, Asset or Risk in one of your customer's
                  tenancies, this is where the notification will be sent. For example, ticketing@deiterate.com
                </p>
              </div>

              <button
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${saveStatus === "saving"
                  ? "bg-primary/70 text-primary-foreground cursor-not-allowed"
                  : "bg-primary text-primary-foreground hover:bg-primary/90"
                  }`}
                onClick={handleSaveTicketingEmail}
                disabled={saveStatus === "saving"}
              >
                {saveStatus === "saving" ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-b-2 border-current rounded-full"></div>
                    Saving...
                  </span>
                ) : saveStatus === "success" ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Saved
                  </span>
                ) : saveStatus === "error" ? (
                  <span className="flex items-center gap-2">
                    <svg className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Error
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <FontAwesomeIcon icon={faEnvelope} />
                    Save Settings
                  </span>
                )}
              </button>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default MSPSettingsSection;
// export { MSPSettingsSection };